import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";

class SeoPage extends Component {
  state = {
    seo_pages: [],
    formData: { id: null, title: "", description: "", keyword: "", status: "" },
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    modalMode: "add",
  };

  componentDidMount() {
    this.fetchSeoPages();

    const modalEl = document.getElementById("seo_pageModal");
    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", () => {
        this.setState({
          formData: {
            id: null,
            title: "",
            description: "",
            keyword: "",
            status: "",
          },
          error: "",
          submitting: false,
          modalMode: "add",
        });
      });
    }
  }

  /** ✅ Fetch all SEO pages */
  fetchSeoPages = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const res = await axios.get("/seo_pages");
      const data = res.data?.data || [];
      this.setState({ seo_pages: Array.isArray(data) ? data : [] });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Failed to load SEO pages.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /** ✅ Handle input changes */
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
    }));
  };

  /** ✅ Open modal (Add / Edit) */
  openModal = (mode = "add", page = null) => {
    const modal = new window.bootstrap.Modal(
      document.getElementById("seo_pageModal")
    );

    if (mode === "edit" && page) {
      this.setState({
        modalMode: "edit",
        formData: {
          id: page.id,
          title: page.title,
          description: page.description,
          keyword: page.keyword,
          status: page.status?.toString() ?? "0",
        },
        error: "",
      });
    } else {
      this.setState({
        modalMode: "add",
        formData: { id: null, title: "", description: "", keyword: "", status: "" },
        error: "",
      });
    }

    modal.show();
  };

  /** ✅ Submit form (Create / Update) */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.title) return this.setState({ error: "Title is required." });
    if (!formData.description)
      return this.setState({ error: "Description is required." });
    if (!formData.keyword)
      return this.setState({ error: "Keyword is required." });
    if (formData.status === "")
      return this.setState({ error: "Status is required." });

    this.setState({ submitting: true, error: "" });

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      if (modalMode === "add") {
        await axios.post("/seo_pages", formData, { headers });
      } else {
        await axios.put(`/seo_pages/${formData.id}`, formData, { headers });
      }

      await this.fetchSeoPages();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("seo_pageModal")
      );
      modal.hide();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "SEO Page added successfully!"
            : "SEO Page updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving SEO Page.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  /** ✅ Confirm delete modal */
  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modal = new window.bootstrap.Modal(
      document.getElementById("deleteModal")
    );
    modal.show();
  };

  /** ✅ Delete SEO Page */
  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/seo_pages/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await this.fetchSeoPages();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("deleteModal")
      );
      modal.hide();

      Swal.fire({
        icon: "success",
        title: "SEO Page deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Failed to delete SEO Page.",
      });
    }
  };

  render() {
    const { seo_pages, formData, error, loading, submitting, modalMode } =
      this.state;

    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="row">
            <div className="col-lg-12">
              <div className="card shadow-none">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <ul className="breadcrumb mb-0">
                    <li className="breadcrumb-item">SEO Pages</li>
                    <li className="breadcrumb-item active">List</li>
                  </ul>
                  <button
                    type="button"
                    className="btn btn-light-warning"
                    onClick={() => this.openModal("add")}
                  >
                    + New SEO Page
                  </button>
                </div>

                <div className="card-body">
                  {error && <div className="alert alert-danger">{error}</div>}
                  {loading ? (
                    <div className="text-center py-4">Loading...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Title</th>
                            <th>Description</th>
                            <th>Keyword</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seo_pages.length ? (
                            seo_pages.map((page, index) => (
                              <tr key={page.id}>
                                <td>{index + 1}</td>
                                <td>{page.title}</td>
                                <td>{page.description}</td>
                                <td>{page.keyword}</td>
                                <td>
                                  {page.status === "1" || page.status === 1 ? (
                                    <span className="badge bg-success">
                                      Active
                                    </span>
                                  ) : (
                                    <span className="badge bg-danger">
                                      Inactive
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-success me-2"
                                    onClick={() => this.openModal("edit", page)}
                                  >
                                    <i className="ph ph-note-pencil"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-light-danger"
                                    onClick={() => this.confirmDelete(page.id)}
                                  >
                                    <i className="ph ph-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center">
                                No SEO pages found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Add/Edit Modal */}
        <div className="modal fade" id="seo_pageModal" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalMode === "add" ? "Add SEO Page" : "Edit SEO Page"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      className="form-control"
                      value={formData.title}
                      onChange={this.handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      className="form-control"
                      rows="3"
                      value={formData.description}
                      onChange={this.handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Keyword</label>
                    <input
                      type="text"
                      name="keyword"
                      className="form-control"
                      value={formData.keyword}
                      onChange={this.handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="status"
                      className="form-select"
                      value={formData.status}
                      onChange={this.handleChange}
                      required
                    >
                      <option value="">Select Status</option>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>

                  {error && <div className="alert alert-danger">{error}</div>}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-light-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Saving..."
                      : modalMode === "add"
                      ? "Add"
                      : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* ✅ Delete Confirmation */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-body text-center p-4">
                <i className="ph ph-warning text-danger f-40 mb-3"></i>
                <h5>Are you sure?</h5>
                <p className="text-muted mb-4">This action cannot be undone.</p>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    data-bs-dismiss="modal"
                  >
                  Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    onClick={this.handleDelete}
                  >
                  Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default SeoPage;
